import { type FC, type PropsWithChildren, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import styles from './MeetingInsights.module.scss'
import { MEETING_INSIGHT_THRESHOLDS } from './meetingInsightsConfig'
import { getVisibleMeetingInsightRows, type VisibleMeetingInsightRows } from './getVisibleMeetingInsightRows'
import { MeetingInsightsCard } from './MeetingInsightsCard'
import { pluralizeMeetingType } from './pluralizeMeetingType'
import { type InsightsDashboardData, TIME_RANGE_LABELS } from '../../../../../../shared/insights'
import { MeetingInsightsEmptyState } from './MeetingInsightsEmptyState/MeetingInsightsEmptyState'
import { Button } from '../../../../../../components/Button/Button'
import { ScopedBlock } from '../../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../../components/ScopedBlock/models'
import { TrackEvent, useTracking } from '../../../common/tracking'
import { ADVISOR_INSIGHTS_TIME_RANGE } from '../../../../../../common/useInsightsData'

interface InsightsRowProps extends PropsWithChildren {
  icon: string
}

const InsightsRow: FC<InsightsRowProps> = ({ icon, children }) => {
  return (
    <div className='d-flex align-items-center zocks-gap-0.75'>
      <div
        className={classNames(
          'd-flex align-items-center justify-content-center flex-shrink-0 zocks-bg-color-gray-100 border zocks-border-color-gray-200 zocks-border-0.5 zocks-text-primary',
          styles.iconBox,
        )}
      >
        <i className={`bi bi-${icon} fs-6`} aria-hidden />
      </div>
      <div className='flex-1 mw-0 zocks-text-gray-900'>{children}</div>
    </div>
  )
}

type MeetingInsightsContentProps = VisibleMeetingInsightRows

const MeetingInsightsContent: FC<MeetingInsightsContentProps> = ({ topTopics, topOptimisticAssetClass, heldAway, referralAsk }) => {
  const navigate = useNavigate()
  const { trackingClient } = useTracking()

  const handleAccountInsightsLinkClick = useCallback(() => {
    trackingClient.track({ eventName: TrackEvent.MEETING_PREP_INSIGHTS_LINK_CLICKED })
    navigate('/insights')
  }, [navigate, trackingClient])

  return (
    <>
      {topTopics.length > 0 && (
        <InsightsRow icon='chat-dots'>
          {topTopics.length === 1 ? (
            <>
              Expect <strong>{topTopics[0]}</strong> to come up
            </>
          ) : (
            <>
              Expect <strong>{topTopics[0]}</strong> and <strong>{topTopics[1]}</strong> to come up
            </>
          )}
        </InsightsRow>
      )}
      {topOptimisticAssetClass && (
        <InsightsRow icon='binoculars'>
          <>
            Clients are most optimistic about <strong>{topOptimisticAssetClass}</strong>
          </>
        </InsightsRow>
      )}
      {heldAway && (
        <InsightsRow icon='bullseye'>
          <>
            Good moment to <strong>probe held-away assets</strong>: {heldAway.agreedPercent}% turn into agreements
          </>
        </InsightsRow>
      )}
      {referralAsk && (
        <InsightsRow icon='people'>
          <>
            Consider <strong>making a referral ask:</strong> {referralAsk.conversionRate}% succeed in these meetings
          </>
        </InsightsRow>
      )}
      <ScopedBlock scope={{ scope: 'insights:read', superScope: [] }} missingScopeAction={MissingScopeAction.HIDE}>
        <Button variant='primaryLinkButton' className='button-medium text-decoration-none align-self-center py-0' onClick={handleAccountInsightsLinkClick}>
          View account insights
          <i className='bi bi-box-arrow-up-right' aria-hidden />
        </Button>
      </ScopedBlock>
    </>
  )
}

interface MeetingInsightsProps {
  data: InsightsDashboardData
  meetingTypeName: string
}

const INSIGHT_TIME_RANGE_LABEL = TIME_RANGE_LABELS[ADVISOR_INSIGHTS_TIME_RANGE]

export const MeetingInsights: FC<MeetingInsightsProps> = ({ data, meetingTypeName }) => {
  const { topTopics, topOptimisticAssetClass, heldAway, referralAsk } = useMemo(
    () => getVisibleMeetingInsightRows({ data, config: MEETING_INSIGHT_THRESHOLDS }),
    [data],
  )

  const hasContent = data.highlights.meetingCount !== 0 && (topTopics.length > 0 || topOptimisticAssetClass || heldAway || referralAsk)
  const hasReliableMeetingCount = data.highlights.meetingCount >= MEETING_INSIGHT_THRESHOLDS.minReliableMeetingCount
  const infoBox =
    hasContent && !hasReliableMeetingCount ? (
      <>
        <strong>Limited data:</strong> Insights based on a few {pluralizeMeetingType(meetingTypeName)} in the last {INSIGHT_TIME_RANGE_LABEL}. More meetings
        will improve accuracy.
      </>
    ) : null

  return (
    <MeetingInsightsCard title='Insights for this meeting' subtitle={`Based on the last ${INSIGHT_TIME_RANGE_LABEL}`} infoBoxContent={infoBox}>
      {hasContent && (
        <MeetingInsightsContent topTopics={topTopics} topOptimisticAssetClass={topOptimisticAssetClass} heldAway={heldAway} referralAsk={referralAsk} />
      )}
      {!hasContent && <MeetingInsightsEmptyState meetingTypeName={meetingTypeName} timeRangeLabel={INSIGHT_TIME_RANGE_LABEL} />}
    </MeetingInsightsCard>
  )
}
